/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { listMarketIntelligenceItems, listNews, listNewsTags } from "../../api/api";
import PageBackButton from "../../components/PageBackButton";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { BrainCircuit, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ExternalLink } from "lucide-react";

function stripHtml(html) {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

function sanitizeHtml(html) {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  tmp.querySelectorAll("script, style, iframe, object, embed, form, input").forEach((el) => el.remove());
  return tmp.innerHTML;
}

function formatTime(ts) {
  try {
    if (!ts) return "-";
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return ts || "-";
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return ts || "-";
  }
}

function getDefaultDateRange() {
  const now = new Date();
  const monthAgo = new Date(now);
  monthAgo.setDate(now.getDate() - 30);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { from: fmt(monthAgo), to: fmt(now) };
}

function parsePositiveInt(value, fallback = 1) {
  const number = Number.parseInt(value, 10);
  return Number.isFinite(number) && number > 0 ? number : fallback;
}

function splitCsv(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function labelize(value) {
  if (!value) return "-";
  return String(value)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatScore(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(2) : "-";
}

const FEEDS = [
  { id: "news", label: "News" },
  { id: "market_intelligence", label: "Market Intelligence" },
];

const FEED_IDS = new Set(FEEDS.map((feed) => feed.id));

const MARKET_INTELLIGENCE_STATUSES = [
  { value: "", label: "All statuses" },
  { value: "ACTIVE", label: "Active" },
  { value: "IGNORED", label: "Ignored" },
  { value: "DUPLICATE", label: "Duplicate" },
  { value: "ERROR", label: "Error" },
  { value: "CORRECTED", label: "Corrected" },
  { value: "REMOVED", label: "Removed" },
];

const MARKET_INTELLIGENCE_PROFILES = [
  { value: "", label: "All profiles" },
  { value: "macro_policy", label: "Macro Policy" },
  { value: "fx_commodities", label: "FX & Commodities" },
  { value: "equities_mega_cap", label: "Mega Cap Equities" },
  { value: "risk_geopolitics", label: "Risk & Geopolitics" },
  { value: "indices_sentiment", label: "Indices & Sentiment" },
  { value: "official_sources", label: "Official Sources" },
  { value: "default", label: "Default" },
];

const AGENT_READY_OPTIONS = [
  { value: "", label: "All readiness" },
  { value: "true", label: "Agent ready" },
  { value: "false", label: "Inbox only" },
];

const FEED_PAGE_COPY = {
  news: {
    title: "News",
    subtitle: "Raw headlines and source content for fundamentals research.",
  },
  market_intelligence: {
    title: "Market Intelligence",
    subtitle: "Stored provider items, readiness, and impact scoring from the ingestion layer.",
  },
};

const selectClassName =
  "w-full rounded-md border border-borderDefault bg-backgroundInput px-3 py-2 text-sm text-textPrimary outline-none focus:border-primary focus:ring-1 focus:ring-primary";

const articleBodyClassName =
  "max-w-none text-sm text-textSecondary leading-relaxed " +
  "[&_a]:text-primary [&_a]:underline " +
  "[&_img]:max-w-full [&_img]:rounded " +
  "[&_ul]:list-disc [&_ul]:pl-5 " +
  "[&_ol]:list-decimal [&_ol]:pl-5 " +
  "[&_p]:mb-2 [&_p]:text-textSecondary " +
  "[&_strong]:text-textPrimary " +
  "[&_h1]:mb-2 [&_h1]:text-lg [&_h1]:font-bold [&_h1]:text-textPrimary " +
  "[&_h2]:mb-2 [&_h2]:text-base [&_h2]:font-bold [&_h2]:text-textPrimary " +
  "[&_h3]:mb-1 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-textPrimary " +
  "[&_blockquote]:border-l-2 [&_blockquote]:border-primary/40 [&_blockquote]:pl-3 [&_blockquote]:italic";

function normalizeFeedParam(value) {
  return FEED_IDS.has(value) ? value : "news";
}

function isMarketMindParam(value) {
  return value === "market_state" || value === "market_mind";
}

function buildMarketMindRedirect(searchParams) {
  const params = new URLSearchParams(searchParams);
  const expanded = params.get("expanded");
  params.delete("feed");
  params.delete("tab");
  params.delete("date_from");
  params.delete("date_to");
  params.delete("source");
  params.delete("tags");
  params.delete("status");
  params.delete("stream_profile");
  params.delete("agent_ready");
  if (expanded) {
    params.set("selected", expanded);
    params.delete("expanded");
  }
  const query = params.toString();
  return `/fundamentals/market-mind${query ? `?${query}` : ""}`;
}

function statusBadgeClass(status) {
  const base = "px-2 py-0.5 rounded-full text-xs whitespace-nowrap";
  if (status === "ACTIVE") return `${base} bg-success/15 text-success`;
  if (status === "IGNORED") return `${base} bg-secondary/40 text-textSecondary`;
  if (status === "ERROR") return `${base} bg-destructive/15 text-destructive`;
  if (status === "DUPLICATE") return `${base} bg-warning/15 text-warning`;
  return `${base} bg-secondary/40 text-textSecondary`;
}

export default function NewsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialDateRange = getDefaultDateRange();
  const feedParam = searchParams.get("feed") || searchParams.get("tab");
  const shouldRedirectToMarketMind = isMarketMindParam(feedParam);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [articles, setArticles] = useState([]);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [feed, setFeed] = useState(() => normalizeFeedParam(feedParam));
  const [page, setPage] = useState(() => parsePositiveInt(searchParams.get("page"), 1));
  const [pageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [dateFrom, setDateFrom] = useState(() => searchParams.get("date_from") || initialDateRange.from);
  const [dateTo, setDateTo] = useState(() => searchParams.get("date_to") || initialDateRange.to);
  const [source, setSource] = useState(() => searchParams.get("source") || "");
  const [selectedTags, setSelectedTags] = useState(() => splitCsv(searchParams.get("tags")));
  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [availableTags, setAvailableTags] = useState([]);
  const [miStatus, setMiStatus] = useState(() => searchParams.get("status") || "");
  const [miProfile, setMiProfile] = useState(() => searchParams.get("stream_profile") || "");
  const [miAgentReady, setMiAgentReady] = useState(() => searchParams.get("agent_ready") || "");

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const isMarketIntelligence = feed === "market_intelligence";
  const tableColSpan = isMarketIntelligence ? 7 : 5;
  const resultLabel = isMarketIntelligence ? "items" : "articles";
  const pageCopy = FEED_PAGE_COPY[feed] || FEED_PAGE_COPY.news;

  const buildRouteParams = (overrides = {}) => {
    const defaultRange = getDefaultDateRange();
    const nextFeed = overrides.feed ?? feed;
    const nextPage = overrides.page ?? page;
    const nextSearch = overrides.search ?? search;
    const params = new URLSearchParams();

    if (nextFeed !== "news") params.set("feed", nextFeed);
    if (nextPage > 1) params.set("page", String(nextPage));
    if (nextSearch) params.set("search", nextSearch);

    const nextDateFrom = overrides.date_from ?? dateFrom;
    const nextDateTo = overrides.date_to ?? dateTo;
    const nextSource = overrides.source ?? source;
    if (nextDateFrom && nextDateFrom !== defaultRange.from) params.set("date_from", nextDateFrom);
    if (nextDateTo && nextDateTo !== defaultRange.to) params.set("date_to", nextDateTo);
    if (nextSource) params.set("source", nextSource);

    if (nextFeed === "market_intelligence") {
      const nextStatus = overrides.status ?? miStatus;
      const nextProfile = overrides.stream_profile ?? miProfile;
      const nextAgentReady = overrides.agent_ready ?? miAgentReady;
      if (nextStatus) params.set("status", nextStatus);
      if (nextProfile) params.set("stream_profile", nextProfile);
      if (nextAgentReady) params.set("agent_ready", nextAgentReady);
    } else {
      const nextTags = overrides.tags ?? selectedTags;
      if (nextTags.length > 0) params.set("tags", nextTags.join(","));
    }

    return params;
  };

  const syncRouteParams = (overrides = {}) => {
    setSearchParams(buildRouteParams(overrides), { replace: true });
  };

  const fetchData = async (overrides = {}) => {
    const currentFeed = overrides.feed ?? feed;
    const currentPage = overrides.page ?? page;
    const currentTags = overrides.tags ?? selectedTags;
    const currentStatus = overrides.status ?? miStatus;
    const currentProfile = overrides.stream_profile ?? miProfile;
    const currentAgentReady = overrides.agent_ready ?? miAgentReady;
    const newsParams = {
      date_from: overrides.date_from ?? dateFrom,
      date_to: overrides.date_to ?? dateTo,
      source: overrides.source ?? source,
      search: (overrides.search ?? search) || undefined,
      page: currentPage,
      page_size: pageSize,
    };

    try {
      setLoading(true);
      setError(null);
      const res =
        currentFeed === "market_intelligence"
          ? await listMarketIntelligenceItems({
              ...newsParams,
              status: currentStatus || undefined,
              stream_profile: currentProfile || undefined,
              agent_ready: currentAgentReady || undefined,
              ordering: "-published_at",
            })
          : await listNews({
              ...newsParams,
              tags: currentTags.length > 0 ? currentTags.join(",") : undefined,
            });

      if (res && typeof res.count === "number" && Array.isArray(res.results)) {
        setArticles(res.results);
        setTotalCount(res.count);
      } else {
        const items = Array.isArray(res) ? res : [];
        setArticles(items);
        setTotalCount(items.length);
      }
    } catch (e) {
      setError(e?.message || (currentFeed === "market_intelligence" ? "Failed to load market intelligence" : "Failed to load news"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (shouldRedirectToMarketMind) return;
    fetchData({ date_from: dateFrom, date_to: dateTo, feed, page });
    listNewsTags()
      .then((tags) => {
        if (Array.isArray(tags)) setAvailableTags(tags);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearFilters = () => {
    const defaultRange = getDefaultDateRange();
    setDateFrom(defaultRange.from);
    setDateTo(defaultRange.to);
    setSource("");
    setSelectedTags([]);
    setSearch("");
    setMiStatus("");
    setMiProfile("");
    setMiAgentReady("");
    setPage(1);
    setExpandedIds(new Set());
    syncRouteParams({
      page: 1,
      feed,
      search: "",
      date_from: defaultRange.from,
      date_to: defaultRange.to,
      source: "",
      tags: [],
      status: "",
      stream_profile: "",
      agent_ready: "",
    });
    fetchData({
      page: 1,
      feed,
      search: "",
      date_from: defaultRange.from,
      date_to: defaultRange.to,
      source: "",
      tags: [],
      status: "",
      stream_profile: "",
      agent_ready: "",
    });
  };

  const applyFilters = () => {
    setPage(1);
    setExpandedIds(new Set());
    syncRouteParams({ page: 1, feed });
    fetchData({ page: 1, feed });
  };

  const changeFeed = (nextFeed) => {
    if (nextFeed === feed) return;
    setFeed(nextFeed);
    setPage(1);
    setExpandedIds(new Set());
    syncRouteParams({ feed: nextFeed, page: 1 });
    fetchData({ feed: nextFeed, page: 1 });
  };

  const goToPage = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setPage(nextPage);
    setExpandedIds(new Set());
    syncRouteParams({ page: nextPage, feed });
    fetchData({ page: nextPage, feed });
  };

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (shouldRedirectToMarketMind) {
    return <Navigate to={buildMarketMindRedirect(searchParams)} replace />;
  }

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="flex items-start gap-3">
          <PageBackButton label="Back to Research" to="/research" preferHistory={false} hideLabelOnMobile />
          <div>
            <h1 className="text-2xl font-semibold text-textPrimary">{pageCopy.title}</h1>
            <p className="mt-1 text-sm text-textSecondary">{pageCopy.subtitle}</p>
          </div>
        </div>
        <Link
          to="/fundamentals/market-mind"
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-borderDefault bg-backgroundSurface px-3 py-2 text-sm font-medium text-textPrimary transition hover:border-primary/40 hover:bg-primary/10"
        >
          <BrainCircuit className="h-4 w-4 text-primary" />
          Market Mind
        </Link>
      </div>

      <Card className="space-y-4 rounded-lg p-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="inline-flex w-fit rounded-md border border-borderDefault bg-secondary/20 p-1">
            {FEEDS.map((option) => {
              const active = feed === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => changeFeed(option.id)}
                  className={`rounded px-3 py-1.5 text-sm font-medium transition-colors ${
                    active
                      ? "bg-primary text-textAccent"
                      : "text-textSecondary hover:bg-secondary/40 hover:text-textPrimary"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <div className="text-xs text-textSecondary">
            {isMarketIntelligence ? "Stored provider items and source readiness." : "Raw headline feed and article content."}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div>
            <label className="mb-1 block text-sm">From</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-sm">To</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-sm">Source</label>
            <Input
              placeholder={isMarketIntelligence ? "e.g. Reuters, Investing.com" : "e.g. Forex Factory"}
              value={source}
              onChange={(e) => setSource(e.target.value)}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm">Search</label>
            <Input
              placeholder={isMarketIntelligence ? "Search stored items" : "Search headlines and content"}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {isMarketIntelligence ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm">Status</label>
              <select className={selectClassName} value={miStatus} onChange={(e) => setMiStatus(e.target.value)}>
                {MARKET_INTELLIGENCE_STATUSES.map((option) => (
                  <option key={option.value || "all"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm">Profile</label>
              <select className={selectClassName} value={miProfile} onChange={(e) => setMiProfile(e.target.value)}>
                {MARKET_INTELLIGENCE_PROFILES.map((option) => (
                  <option key={option.value || "all"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm">Readiness</label>
              <select className={selectClassName} value={miAgentReady} onChange={(e) => setMiAgentReady(e.target.value)}>
                {AGENT_READY_OPTIONS.map((option) => (
                  <option key={option.value || "all"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : null}

        {!isMarketIntelligence && availableTags.length > 0 ? (
          <div>
            <label className="mb-1 block text-sm">Tags</label>
            <div className="flex flex-wrap gap-1.5">
              {availableTags.map((tag) => {
                const active = selectedTags.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => setSelectedTags((prev) => (active ? prev.filter((item) => item !== tag) : [...prev, tag]))}
                    className={`rounded-full border px-2 py-0.5 text-xs transition-colors ${
                      active
                        ? "border-primary bg-primary text-textAccent"
                        : "border-borderDefault bg-secondary/30 text-textSecondary hover:bg-secondary/60"
                    }`}
                  >
                    {tag.replace(/_/g, " ")}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="flex gap-2">
          <Button onClick={applyFilters}>Apply</Button>
          <Button variant="secondary" onClick={clearFilters}>
            Clear
          </Button>
        </div>
      </Card>

      <Card className="overflow-hidden rounded-lg p-0">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-secondary/50">
              <tr>
                <th className="p-3 text-left">Time</th>
                {isMarketIntelligence ? (
                  <>
                    <th className="p-3 text-left">Provider</th>
                    <th className="p-3 text-left">Source</th>
                    <th className="p-3 text-left">Title</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">Impact</th>
                    <th className="p-3 text-left">Link</th>
                  </>
                ) : (
                  <>
                    <th className="p-3 text-left">Source</th>
                    <th className="p-3 text-left">Headline</th>
                    <th className="p-3 text-left">Tags</th>
                    <th className="p-3 text-left">Link</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="p-3 text-textSecondary" colSpan={tableColSpan}>
                    Loading...
                  </td>
                </tr>
              ) : null}
              {error && !loading ? (
                <tr>
                  <td className="p-3 text-destructive" colSpan={tableColSpan}>
                    {error}
                  </td>
                </tr>
              ) : null}
              {!loading && !error && articles.length === 0 ? (
                <tr>
                  <td className="p-3 text-textSecondary" colSpan={tableColSpan}>
                    {isMarketIntelligence ? "No market intelligence items found" : "No news articles found"}
                  </td>
                </tr>
              ) : null}
              {!loading && !error
                ? articles.map((article) => {
                    const isExpanded = expandedIds.has(article.id);
                    const content = article.content || article.body_excerpt || "";
                    const hasContent = !!content;
                    const plainPreview = stripHtml(content);

                    if (isMarketIntelligence) {
                      return (
                        <React.Fragment key={article.id}>
                          <tr
                            className={`border-t border-borderDefault hover:bg-secondary/20 ${hasContent ? "cursor-pointer" : ""}`}
                            onClick={() => hasContent && toggleExpanded(article.id)}
                          >
                            <td className="whitespace-nowrap p-3">{formatTime(article.published_at || article.ingested_at)}</td>
                            <td className="p-3">
                              <div className="flex flex-col gap-1">
                                <span className="w-fit rounded-full bg-primary/20 px-2 py-0.5 text-xs text-primary">
                                  {article.provider || "-"}
                                </span>
                                <span className="whitespace-nowrap text-[11px] text-textSecondary">
                                  {labelize(article.stream_profile)}
                                </span>
                              </div>
                            </td>
                            <td className="whitespace-nowrap p-3">{article.source_name || "-"}</td>
                            <td className="p-3">
                              <div className="max-w-xl">
                                <div className="flex items-start gap-2">
                                  <p className="flex-1 font-medium">{article.title || "-"}</p>
                                  {hasContent ? (
                                    <span className="mt-0.5 shrink-0 text-textSecondary">
                                      {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                    </span>
                                  ) : null}
                                </div>
                                {!isExpanded && plainPreview ? (
                                  <p className="mt-1 line-clamp-1 text-xs text-textSecondary">{plainPreview}</p>
                                ) : null}
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="flex flex-col gap-1">
                                <span className={statusBadgeClass(article.status)}>{labelize(article.status)}</span>
                                {article.is_agent_ready ? (
                                  <span className="whitespace-nowrap text-[11px] text-success">Agent ready</span>
                                ) : null}
                              </div>
                            </td>
                            <td className="whitespace-nowrap p-3">{formatScore(article.materiality_score)}</td>
                            <td className="p-3">
                              {article.url ? (
                                <a
                                  href={article.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Open <ExternalLink className="h-3 w-3" />
                                </a>
                              ) : (
                                "-"
                              )}
                            </td>
                          </tr>
                          {isExpanded && hasContent ? (
                            <tr className="bg-secondary/10">
                              <td colSpan={tableColSpan} className="px-6 py-4">
                                <div className={articleBodyClassName} dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }} />
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    }

                    return (
                      <React.Fragment key={article.id}>
                        <tr
                          className={`border-t border-borderDefault hover:bg-secondary/20 ${hasContent ? "cursor-pointer" : ""}`}
                          onClick={() => hasContent && toggleExpanded(article.id)}
                        >
                          <td className="whitespace-nowrap p-3">{formatTime(article.time)}</td>
                          <td className="whitespace-nowrap p-3">
                            <span className="rounded-full bg-primary/20 px-2 py-0.5 text-xs text-primary">
                              {article.source || "-"}
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="max-w-lg">
                              <div className="flex items-start gap-2">
                                <p className="flex-1 font-medium">{article.headline || "-"}</p>
                                {hasContent ? (
                                  <span className="mt-0.5 shrink-0 text-textSecondary">
                                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                  </span>
                                ) : null}
                              </div>
                              {!isExpanded && plainPreview ? (
                                <p className="mt-1 line-clamp-1 text-xs text-textSecondary">{plainPreview}</p>
                              ) : null}
                            </div>
                          </td>
                          <td className="p-3">
                            {article.tags && article.tags.length > 0 ? (
                              <div className="flex max-w-[200px] flex-wrap gap-1">
                                {article.tags.map((tag) => (
                                  <span
                                    key={tag}
                                    className="whitespace-nowrap rounded bg-accent/20 px-1.5 py-0.5 text-[10px] text-accent"
                                  >
                                    {tag.replace(/_/g, " ")}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-textSecondary">-</span>
                            )}
                          </td>
                          <td className="p-3">
                            {article.url ? (
                              <a
                                href={article.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Open <ExternalLink className="h-3 w-3" />
                              </a>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                        {isExpanded && hasContent ? (
                          <tr className="bg-secondary/10">
                            <td colSpan={tableColSpan} className="px-6 py-4">
                              <div className={articleBodyClassName} dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }} />
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })
                : null}
            </tbody>
          </table>
        </div>

        {totalCount > 0 ? (
          <div className="flex flex-col gap-3 border-t border-borderDefault px-4 py-3 md:flex-row md:items-center md:justify-between">
            <span className="text-sm text-textSecondary">
              {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, totalCount)} of {totalCount.toLocaleString()} {resultLabel}
            </span>
            <div className="flex flex-wrap items-center gap-1">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => goToPage(1)} className="px-2">
                First
              </Button>
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => goToPage(page - 1)} className="px-2">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {(() => {
                const pages = [];
                let start = Math.max(1, page - 2);
                let end = Math.min(totalPages, start + 4);
                start = Math.max(1, end - 4);
                for (let i = start; i <= end; i += 1) {
                  pages.push(
                    <Button
                      key={i}
                      variant={i === page ? "default" : "ghost"}
                      size="sm"
                      className="min-w-[2rem] px-3"
                      onClick={() => goToPage(i)}
                    >
                      {i}
                    </Button>
                  );
                }
                return pages;
              })()}
              <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => goToPage(page + 1)} className="px-2">
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => goToPage(totalPages)} className="px-2">
                Last
              </Button>
            </div>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
